<template>
  <OptimizationDialogHandler
    ref="dialogHandler"
    @hide="(id) => hideOptimization(id)"
    @unhide="(id) => unhideOptimization(id)" />

  <DeleteConfirmDialog
    v-model:visible="hideOptimizationVisible"
    :title="i18n.t('hideOptimization')"
    :loading="hideOptimizationLoading"
    :delete-btn-label="i18n.t('submit')"
    @cancel="hideOptimizationVisible = false"
    @delete="hideOptimization(optimizationToHide)">
    <span>{{ i18n.t('hideOptimizationInfo') }}</span>
  </DeleteConfirmDialog>

  <SmTable
    :data="props.optimizations"
    :columns="columns"
    :default-sorting="{
      by: 'savings',
      asc: true,
    }"
    style="max-height: calc(100% - 105px)"
    key-field="_id"
    :contain-strict="true"
    @scroll-end-reached="emit('scroll-end-reached')"
    @cell-click="handleCellClick">
    <template #application="{ row }">
      <div v-if="row.application_software_name" class="flex items-center gap-4">
        <ApplicationIcon
          :application-name="row.application_software_name"
          class="shrink-0"
          :application-type="row.application_type" />
        <TextMultiline
          :text="
            row.application_display_name || row.application_software_name
          " />
      </div>
      <div
        v-else-if="row.references.data_source_id"
        class="flex items-center gap-4">
        <ApplicationIcon
          v-if="getDataSource(row.references.data_source_id)?.name"
          :application-name="
            getDataSource(row.references.data_source_id)?.name || ''
          "
          class="shrink-0" />
        <TextMultiline
          :text="getDataSource(row.references.data_source_id)?.name" />
      </div>
    </template>

    <template #person="{ row }">
      <TextMultiline
        v-if="row.references.account_id"
        :text="getPersonName(row)" />
    </template>

    <template #reason="{ row }">
      <TextMultiline
        :text="getReason(row.type as OptimizationType, row.data)" />
    </template>

    <template #savings="{ row }">
      <TextMultiline v-if="!row.deprecated">
        {{ getCorrectedPrice(row.savings * 12) }}
      </TextMultiline>
      <span v-else></span>
    </template>

    <!-- Actions -->
    <template #action="{ row }">
      <SmDropdown trigger="click">
        <SmDropdownItem
          v-if="!row.deprecated"
          :icon="Info"
          @click="openDialog(row)">
          {{ i18n.t('showDetails') }}
        </SmDropdownItem>
        <SmDropdownItem
          v-if="!row.hidden"
          :icon="EyeOff"
          @click="openConfirmDialog(row)">
          {{ i18n.t('views.optimizations.table.actions.hideOptimization') }}
        </SmDropdownItem>
        <SmDropdownItem v-else :icon="Eye" @click="unhideOptimization(row._id)">
          {{ i18n.t('unhideOptimization') }}
        </SmDropdownItem>
      </SmDropdown>
    </template>

    <template #footer-application>
      <TextMultiline class="text-muted" :text="optimizationCountString" />
    </template>

    <template #footer-savings>
      <TextMultiline
        class="overflow-visible whitespace-nowrap text-muted"
        :text="getCorrectedPrice(totalSavings)" />
    </template>
  </SmTable>
</template>

<script setup lang="ts">
  import {
    DataSource,
    DataSourcesService,
    OptimizationType,
    SoftwareOut,
  } from '@/client'
  import { Column } from '@/components/sm/SmTable.types'
  import { getReason } from '@/utils/optimizations'
  import {
    OptimizationStore,
    EnrichedOptimizations,
  } from '@/stores/optimization/optimization.store'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import { useI18n } from 'vue-i18n'
  import { sendToast } from '@/components/sm/SmNotification'
  import { capitalize } from '@/utils/formatter/formatter'
  import { Eye, EyeOff, Info } from 'lucide-vue-next'
  import OptimizationDialogHandler from '@/components/optimizations/OptimizationDialogHandler.vue'

  interface Props {
    optimizations: EnrichedOptimizations[]
    filteredCount: number
    totalCount: number
    applications: SoftwareOut[]
  }

  const i18n = useI18n()
  const props = defineProps<Props>()
  const emit = defineEmits(['scroll-end-reached'])

  // ----------------------------------------------------------------------
  // Table Data
  // ----------------------------------------------------------------------

  const optimizationCountString = computed(() => {
    if (props.filteredCount != props.totalCount) {
      return i18n.t('XofY', {
        x: props.filteredCount,
        y: props.totalCount,
      })
    }

    return i18n.t('optimizationsCount', { count: props.filteredCount })
  })

  const totalSavings = computed(() => {
    return props.optimizations.reduce((sum, optimization) => {
      return sum + optimization.savings * 12
    }, 0)
  })

  const columns = computed<Column<EnrichedOptimizations>[]>(() => [
    {
      key: 'application',
      label: capitalize(i18n.t('application')),
      width: 2,
      sortable: true,
      sortFn: applicationSorter,
    },
    {
      key: 'person',
      label: i18n.t('person'),
      width: 2,
      sortable: false,
    },
    {
      key: 'reason',
      label: i18n.t('reason'),
      width: 2,
      sortable: false,
    },
    {
      key: 'savings',
      label: i18n.t('yearlySavings'),
      width: 1,
      sortable: true,
    },
    {
      key: 'action',
      label: '',
      width: '50px',
      sortable: false,
    },
  ])

  function applicationSorter(
    a: EnrichedOptimizations,
    b: EnrichedOptimizations
  ) {
    const aName: string =
      a?.application_display_name || a?.application_software_name
    const bName: string =
      b?.application_display_name || b?.application_software_name

    return aName.localeCompare(bName)
  }

  function handleCellClick(
    _: number,
    columnKey: string,
    row: EnrichedOptimizations
  ) {
    if (columnKey === 'action') return

    openDialog(row)
  }

  // ---------------------------------------------------------------------------
  // Person related
  // ---------------------------------------------------------------------------

  function getPersonName(optimization: EnrichedOptimizations): string {
    return (
      optimization.person_name ||
      [optimization.person_first_name, optimization.person_last_name]
        .filter(Boolean)
        .join(' ') ||
      optimization.person_email ||
      ''
    )
  }

  // ---------------------------------------------------------------------------
  // Load Data Sources
  // ---------------------------------------------------------------------------

  onMounted(() => {
    reloadConnectedDataSources()
  })

  const dataSources = ref<null | DataSource[]>(null)

  function reloadConnectedDataSources() {
    // Get all connected data sources
    DataSourcesService.getDataSourcesApiV1DataSourceDataSourcesGet({}).then(
      (res: DataSource[]) => {
        dataSources.value = res
      }
    )
  }

  function getDataSource(id: string) {
    return (
      dataSources.value?.find((dataSource) => dataSource._id === id) || null
    )
  }

  // ---------------------------------------------------------------------------
  // Handle Dialogs
  // ---------------------------------------------------------------------------

  function openDialog(optimization: EnrichedOptimizations) {
    if (optimization.references.account_id) {
      dialogHandler.value?.showDialog(optimization, optimization.person_id)
    }
  }

  // Optimization Handler
  const dialogHandler = ref<typeof OptimizationDialogHandler>()

  // Hide optimization
  const hideOptimizationVisible = ref<boolean>(false)
  const optimizationToHide = ref<string>('')
  const hideOptimizationLoading = ref<boolean>(false)

  function openConfirmDialog(optimization: EnrichedOptimizations) {
    hideOptimizationVisible.value = true
    optimizationToHide.value = optimization._id
    hideOptimizationLoading.value = false
  }

  async function hideOptimization(id: string) {
    hideOptimizationLoading.value = true
    await OptimizationStore.hideOptimization(id).then(() => {
      sendToast(i18n.t('hideOptimizationSuccess'), undefined, 'success')
      hideOptimizationLoading.value = false
      hideOptimizationVisible.value = false
      dialogHandler.value?.closeDialog()
    })
  }

  async function unhideOptimization(id: string) {
    await OptimizationStore.unhideOptimization(id).then(() => {
      sendToast(
        i18n.t('notifications.optimizationsView.showOptimizationSuccess.title'),
        undefined,
        'success'
      )
      dialogHandler.value?.closeDialog()
    })
  }

  // ensure reset
  watch(
    () => hideOptimizationVisible.value,
    (newVal) => {
      if (!newVal) {
        hideOptimizationLoading.value = false
        optimizationToHide.value = ''
      }
    }
  )
</script>
